import { router } from 'expo-router';
import { useState } from 'react';
import { ActivityIndicator, Linking, Platform, StyleSheet, Text, View } from 'react-native';

import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/Button';
import { Heading, headingStyle } from '@/components/Heading';
import { HighlightedText } from '@/components/HighlightedText';
import { LandingCallout } from '@/components/LandingCallout';
import { SignupForm } from '@/components/SignupForm';
import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import type { PublicSignupDeal } from '@/services/publicSignup';

export interface SignupCardInviter {
  first_name: string | null;
  public_name: string;
}

export interface SignupCardProps {
  deal: PublicSignupDeal | null;
  inviter: SignupCardInviter | null;
  referralToken?: string;
  refId?: string;
  pathSlug?: string;
  loading?: boolean;
}

interface SubmittedDeal {
  offerName: string;
  redirectUrl: string | null;
  slug: string;
  /** Deal was full — account created, but no redirect; show "reopens" state. */
  full: boolean;
}

/**
 * Sign-in pushes from a deal landing carry the deal detail as the
 * post-PIN destination, so an existing user who signs in from here
 * lands back on this deal instead of home.
 */
function pushLogin(dealSlug: string | undefined) {
  if (dealSlug) {
    router.push({ pathname: '/(auth)/login', params: { redirect: `/deals/${dealSlug}` } });
  } else {
    router.push('/(auth)/login');
  }
}

export function SignupCard({
  deal,
  inviter,
  referralToken,
  refId,
  pathSlug,
  loading = false,
}: SignupCardProps) {
  const [submitted, setSubmitted] = useState<SubmittedDeal | null>(null);

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator color={colors.primary} style={styles.loader} />
      </View>
    );
  }

  if (submitted?.full) {
    // Full deal: account was created, but the deal is out of monthly spots.
    // No redirect — tell them it reopens on the 1st and that they're all set.
    return (
      <View style={styles.container}>
        <View style={styles.heading}>
          <Heading size="3xl" style={styles.headingText}>
            You&apos;re in.
          </Heading>
          <Text style={styles.subhead}>
            {submitted.offerName} is full for this month — it reopens on the 1st. Your account is
            all set; we&apos;ll let you know the moment it&apos;s back so you can claim your bonus.
          </Text>
        </View>

        <Text style={styles.pendingNote}>
          Nothing to do right now. Sign in any time to track your bonuses.
        </Text>

        <View style={styles.signInRow}>
          <Text style={styles.signInPrompt}>Already have an account?</Text>
          <Button
            label="Sign in"
            variant="ghost"
            size="sm"
            onPress={() => pushLogin(submitted.slug)}
          />
        </View>
      </View>
    );
  }

  if (submitted) {
    return (
      <View style={styles.container}>
        <View style={styles.heading}>
          <Heading size="3xl" style={styles.headingText}>
            You&apos;re in.
          </Heading>
          <Text style={styles.subhead}>
            Finish your sign-up at {submitted.offerName} — we opened it in{' '}
            {Platform.OS === 'web' ? 'a new tab' : 'your browser'}. If it didn&apos;t open, use
            the button below.
          </Text>
        </View>

        <Button
          label={`Re-open ${submitted.offerName} sign-up`}
          variant="primary"
          fullWidth
          onPress={() => submitted.redirectUrl && void Linking.openURL(submitted.redirectUrl)}
        />

        <Text style={styles.pendingNote}>
          Your bonus usually lands in MarketPryce within 24 hours after you finish. It
          won&apos;t show up instantly — don&apos;t stress if your account looks empty at
          first.
        </Text>

        <View style={styles.signInRow}>
          <Text style={styles.signInPrompt}>Already finished?</Text>
          <Button
            label="Sign in to track your bonus"
            variant="ghost"
            size="sm"
            onPress={() => pushLogin(submitted.slug)}
          />
        </View>
      </View>
    );
  }

  // Prefer the inviter's first name when present; fall back to public_name
  // for legacy users who never filled in their name. Without this fallback
  // the inviter chip silently disappears for the older referrer cohort.
  const inviterDisplayName = inviter?.first_name?.trim() || inviter?.public_name?.trim() || null;
  const dollars = deal ? `$${(deal.user_cut_cents / 100).toFixed(0)}` : null;
  const submitLabel = deal ? 'Claim my bonus' : 'Send me a code';
  const logoUrl = deal?.brand_logo_url ?? null;
  const offerNameWithPeriod = deal ? sentenceWithPeriod(deal.display_name) : null;

  // Server-authored landing copy (Deal#marketing_copy.landing). Each field
  // is optional and falls back to the generic template below, so deals
  // without it (Invest / Crypto / Fetch) still render. `?.` on
  // marketing_copy is deliberate: a pre-landing server omits the key.
  const landing = deal?.marketing_copy?.landing;
  const headline =
    landing?.headline ??
    (deal
      ? inviterDisplayName
        ? `${inviterDisplayName} invited you to get a **${dollars}** bonus from ${deal.display_name}`
        : `Open a new ${deal.display_name} account and get paid **${dollars}** in 24 hours`
      : null);
  const subhead =
    landing?.subhead ??
    (deal
      ? inviterDisplayName
        ? `Enter your info to ensure ${inviterDisplayName} gets credit. Sign up at ${deal.display_name} to claim your bonus.`
        : `Sign up below and we'll send you straight to ${offerNameWithPeriod}`
      : "Tell us a bit about you. We'll text a code to verify your phone.");
  const callout = landing?.callout ?? null;

  const socialProof = deal ? (
    <Text style={styles.socialProof}>1,000+ students already paid</Text>
  ) : null;

  return (
    <View style={styles.container}>
      {deal && (
        <View style={styles.logoRing}>
          <BrandLogo
            url={logoUrl}
            size={56}
            radius={28}
            inset={8}
            background={colors.surface}
            fallback={
              <View style={styles.logoFallback}>
                <Text style={styles.logoFallbackText}>{deal.brand_name.charAt(0)}</Text>
              </View>
            }
          />
        </View>
      )}

      {inviterDisplayName && (
        <View style={styles.inviterChip}>
          <Text style={styles.inviterChipText}>{inviterDisplayName} referred you</Text>
        </View>
      )}

      <View style={styles.heading}>
        {headline ? (
          <HighlightedText
            style={[headingStyle('4xl'), styles.headingText]}
            accessibilityRole="header"
          >
            {headline}
          </HighlightedText>
        ) : (
          <Heading size="3xl" style={styles.headingText}>
            Create your account
          </Heading>
        )}
        <HighlightedText style={styles.subhead}>{subhead}</HighlightedText>
      </View>

      {callout && <LandingCallout title={callout.title} body={callout.body} />}

      {deal && <Text style={styles.formLead}>Submit your information to get started.</Text>}

      <SignupForm
        referralToken={referralToken}
        refId={refId}
        pathSlug={pathSlug}
        submitLabel={submitLabel}
        afterSubmit={socialProof}
        onDealAnchored={(d, redirectUrl) => {
          if (d.full || !redirectUrl) {
            // Full deal — captured the account, but don't hand out a redirect.
            setSubmitted({ offerName: d.display_name, redirectUrl: null, slug: d.slug, full: true });
          } else {
            void Linking.openURL(redirectUrl);
            setSubmitted({ offerName: d.display_name, redirectUrl, slug: d.slug, full: false });
          }
        }}
      />

      <View style={styles.signInRow}>
        <Text style={styles.signInPrompt}>Already have an account?</Text>
        <Button
          label="Sign in"
          variant="ghost"
          size="sm"
          onPress={() => pushLogin(deal?.slug)}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radii.xl,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    gap: spacing.base,
    ...shadows.card,
  },
  loader: { paddingVertical: spacing['2xl'] },
  logoRing: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 2,
    borderColor: colors.electricGreen,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  logoFallback: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.frost,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoFallbackText: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes['2xl'],
    color: colors.deepGreen,
  },
  inviterChip: {
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.xs,
    backgroundColor: colors.accentTint,
    borderRadius: radii.full,
  },
  inviterChipText: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
  },
  heading: {
    gap: spacing.sm,
    alignItems: 'center',
  },
  headingText: { textAlign: 'center' },
  subhead: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    color: colors.text,
    lineHeight: 22,
    textAlign: 'center',
  },
  pendingNote: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textMuted,
    lineHeight: 20,
    textAlign: 'center',
  },
  socialProof: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.sm,
    color: colors.accentText,
    textAlign: 'center',
  },
  formLead: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textMuted,
    textAlign: 'center',
  },
  signInRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  signInPrompt: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textMuted,
  },
});

function sentenceWithPeriod(value: string): string {
  return /[.!?]$/.test(value) ? value : `${value}.`;
}
